// Jun-2026 — GRN Category 1 / Category 2 masters (multi-split enhancement).
// GLOBAL masters (no project scoping) used across GRN lines, Labour
// Attendance, Labour Payments and PO Payment Requests. Managed by admins /
// Project GRN approvers. Deactivated values disappear from new-row dropdowns
// but historical lines keep their snapshot.
// The inner panel is shared by this modal AND the standalone
// /category-master sidebar page (Aug-2026, user request "Category Master").
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Pencil, Check, Tags } from "lucide-react";

const inputCls = "w-full border border-slate-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-[#F97316]";

export function GrnCategoryMasterPanel({ canManage = true }) {
  const [kind, setKind] = useState("cat1");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ code: "", name: "", description: "" });
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/grn-categories", { params: { kind, include_inactive: true } });
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load categories"); }
  }, [kind]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.code.trim() || !form.name.trim()) { toast.error("Code and Name are mandatory"); return; }
    setBusy(true);
    try {
      await api.post("/grn-categories", { kind, ...form });
      toast.success("Category added");
      setForm({ code: "", name: "", description: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add category"); }
    finally { setBusy(false); }
  };
  const saveEdit = async () => {
    try {
      await api.put(`/grn-categories/${editId}`, edit);
      toast.success("Category updated — historical GRN lines keep their snapshot");
      setEditId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
  };
  const toggleActive = async (c) => {
    const active = c.active !== false;
    try {
      await api.put(`/grn-categories/${c.id}`, { active: !active });
      toast.success(active
        ? "Deactivated — hidden from new GRN rows; existing lines keep the value"
        : "Activated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3" data-testid="grn-cat-panel">
      <div className="flex gap-1">
        {["cat1", "cat2"].map((k) => (
          <button key={k} data-testid={`grn-cat-tab-${k}`} onClick={() => { setKind(k); setEditId(null); }}
            className={`px-3 py-1.5 text-xs font-bold rounded border ${kind === k ? "bg-[#F97316] text-white border-[#F97316]" : "text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
            {k === "cat1" ? "Category 1" : "Category 2"}
          </button>
        ))}
      </div>
      {canManage && (
        <div className="flex items-end gap-2 border border-slate-200 rounded p-3 bg-slate-50">
          <div className="w-28"><div className="text-[9px] uppercase font-bold text-slate-400">Code *</div>
            <input data-testid="grn-cat-add-code" className={inputCls} value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })} /></div>
          <div className="flex-1"><div className="text-[9px] uppercase font-bold text-slate-400">Name *</div>
            <input data-testid="grn-cat-add-name" className={inputCls} value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="flex-1"><div className="text-[9px] uppercase font-bold text-slate-400">Description</div>
            <input data-testid="grn-cat-add-desc" className={inputCls} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <button data-testid="grn-cat-add-btn" onClick={create} disabled={busy}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-white bg-[#F97316] rounded hover:bg-orange-600 disabled:opacity-50">
            <Plus size={12} /> Add
          </button>
        </div>
      )}
      <div className="border border-slate-200 rounded overflow-x-auto">
        <table className="w-full min-w-[560px] text-xs" data-testid="grn-cat-table">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Code</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-left">Status</th>
              {canManage && <th className="px-3 py-2"></th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={canManage ? 5 : 4} className="px-3 py-6 text-center text-slate-400" data-testid="grn-cat-empty">No {kind === "cat1" ? "Category 1" : "Category 2"} values yet{canManage ? " — add the first one above" : ""}</td></tr>
            )}
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-slate-100" data-testid={`grn-cat-row-${c.code}`}>
                {canManage && editId === c.id ? (
                  <>
                    <td className="px-2 py-1.5"><input data-testid="grn-cat-edit-code" className={inputCls} value={edit.code}
                      onChange={(e) => setEdit({ ...edit, code: e.target.value })} /></td>
                    <td className="px-2 py-1.5"><input data-testid="grn-cat-edit-name" className={inputCls} value={edit.name}
                      onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></td>
                    <td className="px-2 py-1.5"><input data-testid="grn-cat-edit-desc" className={inputCls} value={edit.description}
                      onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></td>
                    <td className="px-3 py-1.5 text-slate-400">—</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      <button data-testid="grn-cat-edit-save" onClick={saveEdit} className="text-emerald-700 hover:text-emerald-900 mr-2" title="Save"><Check size={14} /></button>
                      <button data-testid="grn-cat-edit-cancel" onClick={() => setEditId(null)} className="text-slate-400 hover:text-slate-700" title="Cancel"><X size={14} /></button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-3 py-1.5 font-mono font-bold">{c.code}</td>
                    <td className="px-3 py-1.5">{c.name}</td>
                    <td className="px-3 py-1.5 text-slate-500">{c.description || "—"}</td>
                    <td className="px-3 py-1.5">
                      <span className={`inline-block px-1.5 py-0.5 text-[9px] uppercase font-bold border rounded ${c.active !== false ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-slate-100 text-slate-500 border-slate-300"}`}>
                        {c.active !== false ? "Active" : "Inactive"}
                      </span>
                    </td>
                    {canManage && (
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        <button data-testid={`grn-cat-edit-${c.code}`} title="Edit"
                          onClick={() => { setEditId(c.id); setEdit({ code: c.code, name: c.name, description: c.description || "" }); }}
                          className="text-slate-500 hover:text-slate-800 mr-2"><Pencil size={13} /></button>
                        <button data-testid={`grn-cat-toggle-${c.code}`} onClick={() => toggleActive(c)}
                          className={`text-[10px] font-bold px-2 py-0.5 border rounded ${c.active !== false ? "text-rose-700 border-rose-300 hover:bg-rose-50" : "text-emerald-700 border-emerald-300 hover:bg-emerald-50"}`}>
                          {c.active !== false ? "Deactivate" : "Activate"}
                        </button>
                      </td>
                    )}
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-slate-500">
        Global master — one list serves ALL Ops projects (GRN lines, Labour Attendance, Labour Payments, PO Payment Requests). Values are bound by stable ID with a code/name snapshot — renaming or deactivating never changes historical documents.
      </div>
    </div>
  );
}

export default function GrnCategoryMasterModal({ onClose }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center overflow-y-auto py-10" data-testid="grn-cat-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="text-sm font-bold text-slate-900 flex items-center gap-2"><Tags size={15} className="text-[#F97316]" /> GRN Category Masters</div>
          <button data-testid="grn-cat-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        <div className="p-5">
          <GrnCategoryMasterPanel canManage={true} />
        </div>
      </div>
    </div>
  );
}
